// Écrans publics, sans connexion : demande d'accès, confirmation de l'adresse, invitation (choix du mot de passe).
import { useEffect, useState, type FormEvent } from "react";
import { api, unwrap } from "../api/client";
import type { Me } from "../api/types";
import { getLang, rich, t } from "../lib/i18n";
import { Card } from "./AuthScreens";
import { MailLink, useContacts } from "./Contacts";
import { Button, Checkbox, Field, FormError, TextInput } from "./ui";

function Back() {
  return <a className="text-accent hover:underline" href="#/">{t("Retour à la connexion")}</a>;
}

/** privateMode : accès privé, navigateur non autorisé : aucun lien vers la connexion. */
export function SignupScreen({ enabled, privateMode = false }: { enabled: boolean; privateMode?: boolean }) {
  const [f, setF] = useState({ first_name: "", last_name: "", email: "", company: "", usage: "", phone: "", website: "" });
  const [accept, setAccept] = useState(false);
  const [done, setDone] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const info = useContacts()?.info_email;
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF((p) => ({ ...p, [k]: e.target.value }));

  if (!enabled) {
    return <Card title={t("Demande d'accès")} intro={t("Les inscriptions ne sont pas ouvertes sur cette gateway.")}><Back /></Card>;
  }
  if (done) {
    return <Card title={t("Vérifiez votre boîte mail")} intro={done}>{privateMode ? <span /> : <Back />}</Card>;
  }

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      setDone((await unwrap(api.POST("/api/signup", { body: { ...f, accept, lang: getLang() } }))).message);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card wide title={t("Demande d'accès")}
      intro={t("Remplissez ce formulaire : vous recevrez un e-mail pour confirmer votre adresse, puis l'administrateur examinera votre demande.")}>
      <form onSubmit={submit} className="flex flex-col gap-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={t("Prénom")} htmlFor="su-first">
            <TextInput id="su-first" required maxLength={60} autoComplete="given-name" autoFocus value={f.first_name} onChange={set("first_name")} />
          </Field>
          <Field label={t("Nom")} htmlFor="su-last">
            <TextInput id="su-last" required maxLength={60} autoComplete="family-name" value={f.last_name} onChange={set("last_name")} />
          </Field>
        </div>
        <Field label={t("Adresse e-mail")} htmlFor="su-mail" hint={t("Seule cette adresse recevra le lien d'accès.")}>
          <TextInput id="su-mail" type="email" required maxLength={254} autoComplete="email" value={f.email} onChange={set("email")} />
        </Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={t("Société ou organisation (facultatif)")} htmlFor="su-company">
            <TextInput id="su-company" maxLength={100} autoComplete="organization" value={f.company} onChange={set("company")} />
          </Field>
          <Field label={t("Téléphone (facultatif)")} htmlFor="su-phone">
            <TextInput id="su-phone" type="tel" maxLength={30} autoComplete="tel" value={f.phone} onChange={set("phone")} />
          </Field>
        </div>
        <Field label={t("Usage prévu")} htmlFor="su-usage" hint={t("Par exemple : diffuser nos matchs vers YouTube, 2 caméras en SRT.")}>
          <textarea id="su-usage" required minLength={3} maxLength={500} rows={3} value={f.usage} onChange={set("usage")}
            className="w-full rounded-md border border-line bg-bg px-3 py-2 text-ink outline-none focus:border-accent" />
        </Field>
        {/* piège à robots : invisible pour une personne, rempli par les robots qui remplissent tout */}
        <div aria-hidden="true" className="absolute -left-[9999px] h-0 overflow-hidden">
          <label htmlFor="su-site">Site web</label>
          <input id="su-site" tabIndex={-1} autoComplete="off" value={f.website} onChange={set("website")} />
        </div>
        <div>
          <Checkbox id="su-accept" checked={accept} onChange={setAccept}
            label={t("J'accepte que ces informations servent à examiner ma demande et à gérer mon compte.")} />
          <p className="mt-1 pl-6 text-[12px] text-faint">
            {t("Elles ne sont transmises à personne d'autre. Sans confirmation de l'adresse, la demande est effacée sous 24 heures ; vous pouvez demander à l'administrateur d'effacer vos données à tout moment.")}
          </p>
          {info && <p className="mt-1 pl-6 text-[12px] text-faint">{t("Pour toute information :")} <MailLink address={info} /></p>}
        </div>
        <FormError message={error} />
        <Button variant="primary" type="submit" disabled={busy || !accept}>{t("Envoyer la demande")}</Button>
      </form>
      {!privateMode && <p className="text-center text-[13px] text-muted">{t("Déjà un compte ?")} <Back /></p>}
    </Card>
  );
}

/** Lien de l'e-mail : la confirmation demande un clic (un antivirus qui ouvre le lien ne confirme rien). */
export function SignupConfirmScreen({ token }: { token: string }) {
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const confirm = async () => {
    setBusy(true);
    setError(null);
    try {
      setMessage((await unwrap(api.POST("/api/signup/confirm", { body: { token } }))).message);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };
  if (message) return <Card title={t("Adresse confirmée")} intro={message}><Back /></Card>;
  return (
    <Card title={t("Confirmer votre adresse")} intro={t("Un dernier clic pour transmettre votre demande à l'administrateur.")}>
      <FormError message={error} />
      <Button variant="primary" onClick={confirm} disabled={busy}>{t("Confirmer mon adresse")}</Button>
      {error && <a className="text-center text-[13px] text-accent hover:underline" href="#/inscription">{t("Refaire une demande")}</a>}
    </Card>
  );
}

export function InvitationScreen({ token, onDone }: { token: string; onDone: (me: Me) => void }) {
  const [info, setInfo] = useState<{ username: string; first_name: string; gateway: string } | null>(null);
  const [invalid, setInvalid] = useState<string | null>(null);
  const [password, setPassword] = useState("");
  const [again, setAgain] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    unwrap(api.POST("/api/invitation/check", { body: { token } })).then(setInfo).catch((e: Error) => setInvalid(e.message));
  }, [token]);

  if (invalid) return <Card title={t("Invitation")} intro={invalid}><Back /></Card>;
  if (!info) return <Card title={t("Invitation")} intro={t("Vérification du lien…")}><span /></Card>;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (password !== again) {
      setError(t("Les deux mots de passe diffèrent."));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const me = await unwrap(api.POST("/api/invitation/accept", { body: { token, password } }));
      window.location.hash = "#/";
      onDone(me);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card title={t("Bienvenue, {name}", { name: info.first_name })}
      intro={rich("Votre accès à {gateway} est prêt. Choisissez votre mot de passe : vous vous connecterez ensuite avec l'identifiant <id>{username}</id>.",
        { id: (s) => <span className="font-mono text-ink">{s}</span> }, { gateway: info.gateway, username: info.username })}>
      <form onSubmit={submit} className="flex flex-col gap-4">
        <input type="text" autoComplete="username" value={info.username} readOnly hidden />
        <Field label={t("Mot de passe")} htmlFor="inv-pass" hint={t("10 caractères minimum.")}>
          <TextInput id="inv-pass" type="password" autoComplete="new-password" required minLength={10} autoFocus
            value={password} onChange={(e) => setPassword(e.target.value)} />
        </Field>
        <Field label={t("Encore une fois")} htmlFor="inv-again">
          <TextInput id="inv-again" type="password" autoComplete="new-password" required minLength={10}
            value={again} onChange={(e) => setAgain(e.target.value)} />
        </Field>
        <FormError message={error} />
        <Button variant="primary" type="submit" disabled={busy}>{t("Enregistrer et me connecter")}</Button>
      </form>
    </Card>
  );
}
